import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Modal, TextInput, Image, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Colors, SIZES } from '../constants/theme';
import AvisoVistaPrevia from '../components/AvisoVistaPrevia';
// EL PANEL DEL NEGOCIO SE MIRA, NO SE USA, CUANDO SE ENTRA SIN CUENTA.
// Ver la nota de `bloquearSiInvitado` más abajo, en `guardarMenu`.
import { bloquearSiInvitado } from '../services/modoInvitado';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import {
  ofertasDe,
  resumenDeOferta,
  resumenParaElCliente,
  normalizarOferta,
  PORCENTAJE_MAXIMO,
  type Oferta,
} from '../constants/promocionesDeNegocio';
import { storageService } from '../services/storageService';
import { notificationService } from '../services/notificationService';
import { callRefreshStripeOnboarding, callCheckRestaurantConnectStatus, callCancelFoodOrder } from '../services/cloudFunctions';
import { authBridge } from '../services/authBridge';
import { CUSTOMER_CARD_ENABLED, COBROS_EN_LA_APP } from '../constants/featureFlags';
import { whatsappSoporte, SOPORTE_TELEFONO } from '../constants/contacto';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos, aMonto } from '../utils/dinero';

const ESTADOS_CON_CONTACTO_VIVO = ['PENDING', 'PREPARING', 'READY', 'ASSIGNED', 'PICKED_UP'];

/**
 * Nombre y teléfono del cliente en la tarjeta del pedido. H15 (auditoría
 * 16-sep-2026): el teléfono ya no viaja en el documento del pedido (lo leía todo
 * repartidor verificado en el feed READY); vive en `private/contacto`. Solo se
 * escucha mientras el pedido está vivo: en uno entregado o cancelado ya no hace
 * falta llamar a nadie. Los pedidos viejos aún lo traen en el documento.
 */
function ClienteDelPedido({ order }: { order: any }) {
  const vivo = ESTADOS_CON_CONTACTO_VIVO.includes(String(order?.status ?? ''));
  const [telefono, setTelefono] = useState<string | null>(null);
  useEffect(() => {
    if (!vivo || !order?.id) return;
    return firestoreService.escucharPrivadoDelPedido(String(order.id), 'contacto', (data) =>
      setTelefono(String(data?.customerPhone ?? '').trim() || null)
    );
  }, [vivo, order?.id]);
  const mostrado = telefono ?? (String(order?.customerPhone ?? '').trim() || null);
  return (
    <Text style={styles.orderCustomer}>
      👤 {order?.customerName ?? 'Cliente'}{mostrado ? ` (${mostrado})` : ''}
    </Text>
  );
}

const ETIQUETA_METODO_PAGO: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia bancaria',
};

/**
 * V01/V28 — Método de pago y comprobante en la tarjeta del pedido. Al cliente se
 * le dice «el restaurante está revisando tu transferencia», pero aquí no se veía
 * ni el método ni la foto: el dueño aceptaba a ciegas. La foto vive en
 * `private/comprobante` (H15; la regla ya se la deja leer al dueño) y los pedidos
 * viejos la traen en el documento. Se escucha aunque no haya `tieneComprobante`:
 * el cliente puede subirla después desde su recibo.
 */
function PagoDelPedido({ order, onVerComprobante }: { order: any; onVerComprobante: (url: string) => void }) {
  const metodo = String(order?.paymentMethod ?? '');
  const esTransferencia = metodo === 'TRANSFER';
  const vivo = ESTADOS_CON_CONTACTO_VIVO.includes(String(order?.status ?? ''));
  const [urlPrivada, setUrlPrivada] = useState<string | null>(null);
  useEffect(() => {
    if (!esTransferencia || !vivo || !order?.id) return;
    return firestoreService.escucharPrivadoDelPedido(String(order.id), 'comprobante', (data) =>
      setUrlPrivada(String(data?.receiptImage ?? '').trim() || null)
    );
  }, [esTransferencia, vivo, order?.id]);
  const url = urlPrivada ?? (String(order?.receiptImage ?? '').trim() || null);

  let etiqueta = ETIQUETA_METODO_PAGO[metodo] ?? 'Efectivo';
  if (metodo === 'CARD') etiqueta += order?.paymentStatus === 'PAID' ? ' (pagado)' : ' (pago sin confirmar)';

  return (
    <>
      <Text style={styles.orderItems}>💳 Pago: {etiqueta}</Text>
      {esTransferencia && order?.status === 'PENDING' && (
        <Text style={styles.orderItems}>Revisa el comprobante antes de aceptar.</Text>
      )}
      {esTransferencia && vivo && (
        url ? (
          <TouchableOpacity onPress={() => onVerComprobante(url)}>
            <Text style={[styles.orderItems, { color: Colors.primary, fontWeight: 'bold' }]}>🧾 Ver comprobante</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.orderItems}>🧾 Sin comprobante subido</Text>
        )
      )}
    </>
  );
}

export default function RestaurantDashboardScreen() {
  const router = useRouter();
  // Evita redirigir en bucle a /restaurant-registration si el listener del
  // restaurante emite null más de una vez antes de desmontar.
  const redirectedToRegRef = useRef(false);
  // Selectores específicos
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  // Anti doble toque del botón de salir: el cierre completo espera a la red.
  const saliendoRef = useRef(false);
  const [activeTab, setActiveTab] = useState('ORDERS'); // ORDERS, MENU, STATS, PROMOS
  const [orders, setOrders] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [restaurantInfo, setRestaurantInfo] = useState<{
    name?: string;
    status?: string;
    stripeAccountId?: string;
    stripeOnboardingUrl?: string;
    stripeOnboardingCompleted?: boolean;
    stripeChargesEnabled?: boolean;
    // Estado del anuncio. Lo escribe SOLO el servidor al aprobar el pago
    // (`aprobarPagoPublicidad`, functions/src/admin.ts): desde el cliente no se
    // puede encender, y por eso aquí únicamente se muestra.
    isPremium?: boolean;
    adPlan?: string;
    premiumExpiresAt?: any;
  } | null>(null);
  const [refreshingConnect, setRefreshingConnect] = useState(false);
  const [isAddProductModalVisible, setAddProductModalVisible] = useState(false);
  const [precioEditando, setPrecioEditando] = useState<{ index: number; valor: string } | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    prepTime: '',
    isAvailable: true,
    images: [] as string[] // Soporte para múltiples imágenes
  });

  // ── ALARMA DE PEDIDO NUEVO ────────────────────────────────────────────────
  // Antes el restaurante NO tenía ningún aviso: ni push (el disparador del
  // backend solo reaccionaba a cambios de estado, y el pedido nace en PENDING,
  // así que nunca sonaba) ni sonido en pantalla. Un pedido podía entrar y morir
  // en silencio mientras el dueño cocinaba. El chofer sí tenía sonido; el
  // restaurante, que es quien debe responder en dos minutos, no tenía nada.
  //
  // `seenOrderIdsRef` distingue "pedido nuevo de verdad" de "el listener volvió
  // a emitir": sin eso, cualquier cambio en un pedido viejo haría sonar la
  // alarma y el dueño terminaría ignorándola.
  const seenOrderIdsRef = useRef<Set<string>>(new Set());
  const primeraCargaRef = useRef(true);
  const pendientesRef = useRef(0);

  useEffect(() => {
    if (!userFirebaseUid) return;

    // Escuchar en tiempo real los pedidos
    const unsubscribeOrders = firestoreService.listenToRestaurantOrders(userFirebaseUid, (cloudOrders) => {
      setOrders(cloudOrders);

      const pendientes = cloudOrders.filter((o: any) => o.status === 'PENDING');
      pendientesRef.current = pendientes.length;

      // En la primera carga solo memorizamos: abrir la pantalla no debe
      // disparar la alarma por pedidos que el dueño ya conocía.
      if (primeraCargaRef.current) {
        cloudOrders.forEach((o: any) => seenOrderIdsRef.current.add(o.id));
        primeraCargaRef.current = false;
        return;
      }

      const nuevos = pendientes.filter((o: any) => !seenOrderIdsRef.current.has(o.id));
      cloudOrders.forEach((o: any) => seenOrderIdsRef.current.add(o.id));

      if (nuevos.length > 0) {
        const total = nuevos.reduce((acc: number, o: any) => acc + Number(o.total ?? 0), 0);
        notificationService.alertNewOrder(
          nuevos.length === 1 ? '🔔 ¡PEDIDO NUEVO!' : `🔔 ¡${nuevos.length} PEDIDOS NUEVOS!`,
          `${pesos(total)} · Acéptalo para empezar a prepararlo.`,
          { type: 'NEW_FOOD_ORDER' }
        );
      }
    });

    // INSISTENCIA: mientras quede algún pedido sin aceptar, se vuelve a avisar
    // cada 30 s. Es lo que hacen Uber Eats y iFood, y por una razón práctica:
    // un solo aviso se pierde entre el ruido de una cocina.
    const recordatorio = setInterval(() => {
      if (pendientesRef.current > 0) {
        notificationService.alertNewOrder(
          '⏰ Pedido esperando',
          `Tienes ${pendientesRef.current} pedido(s) sin aceptar. El cliente está esperando respuesta.`,
          { type: 'PENDING_ORDER_REMINDER' }
        );
      }
    }, 30000);

    // Escuchar la información del restaurante (menú, promos, nombre, status, Stripe Connect)
    const unsubscribeRestaurant = firestoreService.listenToRestaurantById(userFirebaseUid, (restaurantData) => {
      if (restaurantData) {
        setMenuItems(restaurantData.menu || []);
        // El panel guardaba DOS BANDERAS (`['DELIVERY', 'VIERNES_LOCO']`) en
        // vez de las ofertas, porque solo existían esas dos y con los números
        // clavados en el código. Ahora el negocio crea las suyas, con sus
        // propios mínimos y porcentajes, así que se leen tal cual vienen.
        setOfertas(ofertasDe(restaurantData.activePromotions));
        setRestaurantInfo({
          name: restaurantData.name,
          status: restaurantData.status,
          stripeAccountId: restaurantData.stripeAccountId,
          stripeOnboardingUrl: restaurantData.stripeOnboardingUrl,
          stripeOnboardingCompleted: restaurantData.stripeOnboardingCompleted,
          stripeChargesEnabled: restaurantData.stripeChargesEnabled,
          isPremium: restaurantData.isPremium,
          adPlan: restaurantData.adPlan,
          premiumExpiresAt: restaurantData.premiumExpiresAt,
        });
      } else {
        // FIX AUDIT (CRÍTICO) — El dueño llegó al dashboard SIN restaurante
        // registrado (role-selection iba directo aquí y la pantalla de registro
        // quedaba inalcanzable). Si no hay restaurante, lo mandamos a crearlo.
        // Al registrarlo, el doc existe y el listener ya no cae en esta rama.
        if (!redirectedToRegRef.current) {
          redirectedToRegRef.current = true;
          router.replace('/restaurant-registration');
        }
      }
    });

    return () => {
      unsubscribeOrders();
      unsubscribeRestaurant();
      clearInterval(recordatorio);
      // Si el dueño vuelve a entrar, la próxima carga tampoco debe sonar por
      // pedidos viejos.
      primeraCargaRef.current = true;
    };
  }, [userFirebaseUid]);

  // ── EDICIÓN DEL MENÚ ──────────────────────────────────────────────────────
  // El menú era de SOLO AGREGAR: no había forma de corregir un precio, marcar
  // un plato agotado ni borrarlo. Un restaurante que no puede subir el precio
  // del pollo cuando le sube el proveedor, o apagar un plato que se le acabó,
  // no puede operar — y el cliente termina pidiendo algo que no existe.
  //
  // Las tres funciones reescriben el array `menu` completo, que es como ya lo
  // hacía el alta y lo que permiten las reglas (el dueño puede escribir `menu`,
  // pero no `status` ni los campos de Stripe).
  const guardarMenu = async (nuevoMenu: any[], mensajeError: string) => {
    // ══════════════════════════════════════════════════════════════
    // EN MODO EXPLORAR, ESTE PANEL SE MIRA. NO SE USA.
    // ══════════════════════════════════════════════════════════════
    //
    // Regla del fundador (4-oct-2026): quien entra por «Explorar sin crear
    // cuenta» puede VER cómo se ve el panel de un negocio, pero no usar ni una
    // herramienta. Y el motivo no es de producto, es de seguridad: «cualquier
    // usuario puede engañar a nuestro mensajero con foto falsa si no cuenta
    // con una verificación».
    //
    // Hasta hoy el panel solo pintaba el cartel de vista previa y NINGUNA
    // acción tenía guardia. Lo único que frenaba era el `if (!userFirebaseUid)`
    // de aquí abajo: un `return` mudo en unos sitios y un «Sesión requerida»
    // en otros. Eso no es un candado, es un botón que no hace nada — y es
    // justo el motivo de rechazo vc13 de Google Play.
    //
    // `bloquearSiInvitado` es el mismo guardia que ya usan el mapa, el perfil,
    // el carrito y la billetera: explica por qué y ofrece crear la cuenta.
    if (bloquearSiInvitado('publicar tu menú')) return;
    if (!userFirebaseUid) return;
    const anterior = menuItems;
    setMenuItems(nuevoMenu); // respuesta inmediata en pantalla
    try {
      await firestoreService.updateRestaurantMenu(userFirebaseUid, nuevoMenu);
    } catch (e) {
      console.error('[menu] no se pudo guardar:', e);
      setMenuItems(anterior); // revertir: que la pantalla no mienta
      Alert.alert('No se pudo guardar', mensajeError);
    }
  };

  // ── PROMOCIONES ──────────────────────────────────────────────
  //
  // Los dos botones tenían el MISMO código copiado, con los textos de las dos
  // promociones escritos a mano DOS VECES CADA UNO (cuatro literales para dos
  // promociones). Y lo que decían no era lo que pasaba: «En pedidos mayores a
  // RD$500» cuando no había ningún mínimo implementado en ninguna parte, y
  // «Viernes Loco» sin que nada mirara nunca qué día era. El texto vive ahora
  // en `constants/promocionesDeNegocio.ts`, al lado de la regla que lo cumple.
  //
  // Dos cosas más que faltaban aquí:
  //
  //  1. CONFIRMAR. Un toque encendía una promoción que le cuesta dinero al
  //     negocio, sin decirle cuánto ni poder arrepentirse. Ahora se le dice
  //     qué va a asumir, con el número, antes de activarla.
  //  2. REVERTIR. `setActivePromos(nuevos)` se hacía ANTES del `await`, y el
  //     `await` no tenía try/catch: si la escritura fallaba, el botón se
  //     quedaba en ACTIVO para siempre con la promoción sin guardar — la
  //     misma mentira de pantalla que ya se cerró en el menú con
  //     `guardarMenu`, y que aquí había quedado abierta.
  // ── QUE LA PESTAÑA ELEGIDA SE VEA ────────────────────────────────
  //
  // La fila se desliza, pero al tocar una pestaña de la derecha se quedaba a
  // medias o fuera de cuadro: el dueño tocaba «Publicidad» y seguía viendo
  // «Pedidos» subrayado en pantalla, sin saber dónde había caído. Ahora la
  // pestaña elegida se trae sola, centrada en lo que se pueda.
  const filaDePestanas = useRef<ScrollView | null>(null);
  const medidaDePestana = useRef<Record<string, { x: number; width: number }>>({});
  const anchoDeLaFila = useRef(0);

  const irAPestana = (clave: string) => {
    setActiveTab(clave as any);
    const m = medidaDePestana.current[clave];
    const visible = anchoDeLaFila.current;
    if (!m || !visible) return;
    // Centrarla si cabe; si no, al menos pegarla al borde izquierdo.
    const destino = Math.max(0, m.x - Math.max(0, (visible - m.width) / 2));
    filaDePestanas.current?.scrollTo({ x: destino, animated: true });
  };

  const [guardandoPromo, setGuardandoPromo] = useState(false);
  const [ofertas, setOfertas] = useState<Oferta[]>([]);
  const [ofertaNueva, setOfertaNueva] = useState<null | {
    tipo: 'DELIVERY_GRATIS' | 'DESCUENTO';
    titulo: string;
    minimo: string;
    modo: 'PORCENTAJE' | 'MONTO';
    porcentaje: string;
    monto: string;
    soloPrimerPedido: boolean;
  }>(null);

  const guardarOfertas = async (nuevas: Oferta[], mensajeError: string) => {
    // Mismo candado que el menú: ver la nota de `guardarMenu`.
    if (bloquearSiInvitado('crear ofertas para tu negocio')) return false;
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión como dueño del negocio para manejar tus ofertas.');
      return false;
    }
    const anteriores = ofertas;
    setOfertas(nuevas);
    setGuardandoPromo(true);
    try {
      await firestoreService.updateRestaurantPromos(userFirebaseUid, nuevas);
      return true;
    } catch (e) {
      console.error('[ofertas] no se pudo guardar:', e);
      // Revertir: que la pantalla no diga ACTIVA sin estarlo. Es el mismo
      // arreglo que ya tenía el menú en `guardarMenu`, y que aquí faltaba:
      // el `setActivePromos(nuevos)` se hacía ANTES del `await`, y el `await`
      // no tenía try/catch. Si la escritura fallaba, el botón se quedaba en
      // ACTIVO para siempre con la oferta sin guardar en ninguna parte.
      setOfertas(anteriores);
      Alert.alert('No se pudo guardar', mensajeError);
      return false;
    } finally {
      setGuardandoPromo(false);
    }
  };

  const borrarOferta = (id: string) => {
    const o = ofertas.find((x) => x.id === id);
    if (!o) return;
    Alert.alert(
      'Quitar esta oferta',
      `«${o.title}» dejará de aplicarse a los pedidos nuevos y de salir en tu ficha. Los pedidos que ya la usaron no cambian.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Quitar',
          style: 'destructive',
          onPress: () =>
            guardarOfertas(
              ofertas.filter((x) => x.id !== id),
              'Tu oferta sigue activa. Revisa tu conexión y vuelve a intentarlo.'
            ),
        },
      ]
    );
  };

  /**
   * Crea la oferta que el negocio acaba de describir.
   *
   * Se valida ANTES de guardar y se le dice qué falta: `normalizarOferta`
   * descarta en silencio una oferta sin número —es lo correcto para el
   * servidor, mejor que no exista que que mienta— y aquí ese silencio se vería
   * como un botón que no hace nada.
   */
  const crearOferta = async () => {
    const f = ofertaNueva;
    if (!f) return;
    const titulo = f.titulo.trim();
    if (titulo.length < 3) {
      Alert.alert(
        'Ponle un nombre',
        'Es lo que el cliente va a leer en tu ficha. Por ejemplo: «Delivery gratis este mes».'
      );
      return;
    }
    const minimo = Math.max(0, Math.round(Number(f.minimo) || 0));

    let extra: Partial<Oferta> = {};
    if (f.tipo === 'DESCUENTO') {
      if (f.modo === 'PORCENTAJE') {
        const pct = Math.round(Number(f.porcentaje) || 0);
        if (pct < 1) {
          Alert.alert('Falta el descuento', 'Escribe qué porcentaje vas a descontar.');
          return;
        }
        if (pct > PORCENTAJE_MAXIMO) {
          // El tope es TÉCNICO y se le explica como tal, no como una regla
          // nuestra sobre su negocio: con el 100% el pedido sale en RD$0 y
          // Stripe no puede cobrar cero.
          Alert.alert(
            `El máximo es ${PORCENTAJE_MAXIMO}%`,
            'Con un descuento mayor el pedido saldría en cero y el cobro con tarjeta no se puede procesar. Si quieres regalar un pedido completo, arréglalo directo con el cliente.'
          );
          return;
        }
        extra = { porcentaje: pct };
      } else {
        const monto = Math.round(Number(f.monto) || 0);
        if (monto < 1) {
          Alert.alert('Falta el descuento', 'Escribe cuántos pesos vas a descontar.');
          return;
        }
        extra = { monto };
      }
    }

    const candidata = normalizarOferta({
      id: `of${Date.now().toString(36)}`,
      type: f.tipo,
      title: titulo,
      description: '',
      icon: f.tipo === 'DELIVERY_GRATIS' ? '\U0001F6F5' : '\U0001F3F7',
      color: f.tipo === 'DELIVERY_GRATIS' ? '#00C853' : '#D32F2F',
      minimo,
      soloPrimerPedido: f.soloPrimerPedido,
      ...extra,
    });
    if (!candidata) {
      Alert.alert('Revisa los datos', 'Con esos valores la oferta no descontaría nada.');
      return;
    }

    // La descripción que lee el cliente se GENERA de los mismos números con
    // los que se calcula, no se escribe a mano. Es justo lo que fallaba antes:
    // el texto decía «En pedidos mayores a RD$500» y en el código no existía
    // ningún mínimo.
    const paraGuardar: Oferta = { ...candidata, description: resumenParaElCliente(candidata) };

    const ok = await guardarOfertas(
      [...ofertas, paraGuardar],
      'Tu oferta no se guardó. Revisa tu conexión y vuelve a intentarlo — lo que escribiste sigue aquí.'
    );
    if (ok) setOfertaNueva(null); // solo se cierra si de verdad se guardó
  };

  const toggleDisponibilidad = (index: number) => {
    const item = menuItems[index];
    if (!item) return;
    const nuevo = menuItems.map((it, i) =>
      i === index ? { ...it, isAvailable: !it.isAvailable } : it
    );
    guardarMenu(nuevo, 'El plato sigue como estaba. Revisa tu conexión.');
  };

  // Se usa un modal propio y no Alert.prompt porque Alert.prompt SOLO existe en
  // iOS: en Android no hace nada y el botón habría quedado muerto.
  const cambiarPrecio = (index: number) => {
    const item = menuItems[index];
    if (!item) return;
    setPrecioEditando({ index, valor: String(item.price ?? '') });
  };

  const confirmarPrecio = () => {
    if (!precioEditando) return;
    // `replace(',', '.')` arreglaba el decimal y ROMPIA el millar: "1,500"
    // quedaba en 1.5, y el guardarrail de abajo no lo cazaba porque 1.5 es
    // finito y mayor que cero. El plato se guardaba a RD$1.50. `aMonto` lleva
    // el criterio de la casa, el mismo que `pesos()` ya usaba para pintar.
    const n = aMonto(precioEditando.valor);
    if (!Number.isFinite(n) || n <= 0) {
      Alert.alert('Precio inválido', 'Escribe un número mayor que cero (ej. 450).');
      return;
    }
    const nuevo = menuItems.map((it, i) => (i === precioEditando.index ? { ...it, price: n } : it));
    setPrecioEditando(null);
    guardarMenu(nuevo, 'El precio quedó como estaba. Revisa tu conexión.');
  };

  const borrarPlato = (index: number) => {
    const item = menuItems[index];
    if (!item) return;
    Alert.alert(
      `¿Borrar "${item.name}"?`,
      'Desaparecerá de tu menú y los clientes ya no podrán pedirlo. Si solo se te acabó por hoy, mejor márcalo como AGOTADO.',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Borrar',
          style: 'destructive',
          onPress: () => {
            const nuevo = menuItems.filter((_, i) => i !== index);
            guardarMenu(nuevo, 'El plato sigue en tu menú. Revisa tu conexión.');
          },
        },
      ]
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // PUBLICIDAD — LA CONTRATACIÓN SALIÓ DE LA APP (decisión del fundador)
  // ═════════════════════════════════════════════════════════════════════════
  //
  // QUÉ HABÍA AQUÍ: el restaurante elegía plan (RD$1,000 / RD$2,000 por 30
  // días), veía tres cuentas bancarias, subía la foto del comprobante y, si
  // prefería, pulsaba "Cobrar a mi tarjeta guardada". Todo DENTRO de la app.
  //
  // POR QUÉ SE FUE. Lo que se compraba —posición #1 en la categoría, etiqueta
  // de "Recomendado" y banner— se consume dentro de la propia app. Para Google
  // eso es contenido digital, y su Política de Pagos obliga a cobrarlo con la
  // facturación de Google Play; cobrarlo por transferencia, o mandar al usuario
  // fuera a pagarlo, es motivo de rechazo por sí solo. Con dos rechazos encima,
  // no es un riesgo que se pueda correr por una función opcional.
  //
  // QUÉ QUEDA. El restaurante SIGUE viendo su estado (si tiene anuncio activo y
  // cuándo vence) porque eso es información suya, no una compra. Para
  // contratarlo se habla con nosotros por WhatsApp y se cierra fuera de la app.
  //
  // LO QUE NO SE TOCÓ: `crearPagoPublicidad` y `aprobarPagoPublicidad`
  // (functions/src/admin.ts) siguen vivas — son las que usa el panel de
  // administración para registrar el pago y encender el anuncio.
  const contratarAnuncioPorWhatsApp = () => {
    const nombre = restaurantInfo?.name?.trim();
    Linking.openURL(
      whatsappSoporte(
        `Hola, soy${nombre ? ` de ${nombre}` : ''} y quiero información para contratar publicidad en Go Safely.`
      )
    ).catch(() => {
      // Sin WhatsApp instalado el botón no puede quedarse mudo: se da el
      // número, que sale de constants/contacto.ts (nunca escrito a mano).
      Alert.alert(
        'No pudimos abrir WhatsApp',
        `Llámanos o escríbenos al ${SOPORTE_TELEFONO} y te explicamos los planes de publicidad.`
      );
    });
  };

  const updateOrderStatus = async (id: string, newStatus: string) => {
    // Un pedido es de un cliente de verdad y hay dinero y comida de por medio.
    // Ver la nota de `guardarMenu`.
    if (bloquearSiInvitado('manejar los pedidos de tu negocio')) return;
    try {
      await firestoreService.updateFoodOrderStatus(id, newStatus);
      if (newStatus === 'READY') {
        Alert.alert('Buscando Repartidor', 'El sistema ha notificado a los motoristas cercanos de Go Safely para que pasen a recoger la orden.');
      }
    } catch (error) {
      console.error("Error al actualizar estado:", error);
      Alert.alert("Error", "No pudimos actualizar el estado del pedido.");
    }
  };

  // H65, auditoría 16-sep-2026 — Acepta VARIOS estados. Antes solo se llamaba
  // con PENDING, PREPARING y READY, así que en el instante en que un
  // repartidor tomaba el pedido (READY → ASSIGNED) este desaparecía de todas
  // las secciones: cuando el repartidor llegaba al mostrador, el restaurante no
  // tenía en pantalla qué pedido se llevaba ni si ya lo habían recogido. El
  // listener SÍ bajaba esos pedidos; era solo el filtro de pantalla.
  //
  // `soloHoy` es para los terminados: el listener trae los 50 más recientes, y
  // sin este corte la sección se llenaría de entregas de otros días.
  const renderOrdersList = (statusFilter: string | string[], opciones?: { soloHoy?: boolean }) => {
    const estados = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
    const inicioDeHoy = new Date();
    inicioDeHoy.setHours(0, 0, 0, 0);
    const msDe = (v: any): number | null => {
      const ms = typeof v?.toMillis === 'function' ? v.toMillis() : null;
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    };
    const filtered = orders.filter((o) => {
      if (!estados.includes(o.status)) return false;
      if (!opciones?.soloHoy) return true;
      // La fecha en que TERMINÓ manda; si no viene, la de creación. Sin
      // ninguna de las dos no se puede decir que sea de hoy: no se muestra.
      const ms = msDe(o.deliveredAt) ?? msDe(o.cancelledAt) ?? msDe(o.createdAt);
      return ms !== null && ms >= inicioDeHoy.getTime();
    });

    if (filtered.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>No hay pedidos en esta sección.</Text>
        </View>
      );
    }

    return filtered.map(order => (
      <View key={order.id} style={styles.orderCard}>
        <View style={styles.orderHeader}>
          <Text style={styles.orderId}>Orden #{order.id.slice(-6).toUpperCase()}</Text>
          <Text style={styles.orderTime}>{order.createdAt ? new Date(order.createdAt.toMillis()).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' }) : 'Ahora'}</Text>
        </View>
        <ClienteDelPedido order={order} />
        <PagoDelPedido order={order} onVerComprobante={setFullScreenImage} />
        <Text style={styles.orderItems}>📦 {order.items}</Text>
        <Text style={styles.orderTotal}>💰 {pesos(order.total)}</Text>
        
        <View style={styles.orderActions}>
          {order.status === 'PENDING' && (
            <>
              <TouchableOpacity style={[styles.actionBtn, { backgroundColor: Colors.primary, flex: 1 }]} onPress={() => updateOrderStatus(order.id, 'PREPARING')}>
                <Text style={styles.actionBtnText}>Aceptar y Preparar</Text>
              </TouchableOpacity>
              {/* FIX AUDIT FOOD — Antes el "Rechazar" hacía un update CRUDO a
                  status=CANCELLED: NO reembolsaba la tarjeta, NO seteaba
                  cancelledBy y NO notificaba (el comentario viejo mentía). Con
                  captura automática, el cliente quedaba cobrado SIN reembolso.
                  Ahora usa la callable cancelFoodOrder, que: (a) dispara el
                  refund Stripe si pagó con tarjeta, (b) setea cancelledBy=
                  RESTAURANT (para la multa por cancelación) y (c) notifica al
                  cliente/repartidor. */}
              <TouchableOpacity
                style={[styles.actionBtn, { backgroundColor: '#FF3B30', width: 100, marginLeft: 8 }]}
                onPress={() => {
                  Alert.alert(
                    'Rechazar orden',
                    '¿Estás seguro? El cliente será notificado y, si pagó con tarjeta, su reembolso se procesa automáticamente.',
                    [
                      { text: 'No', style: 'cancel' },
                      {
                        text: 'Rechazar',
                        style: 'destructive',
                        onPress: async () => {
                          try {
                            const res: any = await callCancelFoodOrder({ orderId: order.id, reason: 'Rechazado por el negocio' });
                            const msg = res?.data?.message || 'Orden rechazada.';
                            Alert.alert('Orden rechazada', msg);
                          } catch (e: any) {
                            Alert.alert('Error', mensajeDeError(e, 'No pudimos rechazar la orden. Intenta de nuevo en un momento.'));
                          }
                        },
                      },
                    ]
                  );
                }}
              >
                <Text style={styles.actionBtnText}>Rechazar</Text>
              </TouchableOpacity>
            </>
          )}
          {order.status === 'PREPARING' && (
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: '#00C853' }]} onPress={() => updateOrderStatus(order.id, 'READY')}>
              <Text style={styles.actionBtnText}>Marcar como Listo</Text>
            </TouchableOpacity>
          )}
          {order.status === 'READY' && (
            <View style={[styles.actionBtn, { backgroundColor: '#FF9500' }]}>
              <Text style={styles.actionBtnText}>Esperando Repartidor...</Text>
            </View>
          )}
          {/* Mismos colores que ya usa el recibo del cliente para estos estados
              (order-receipt.tsx), para que las dos pantallas digan lo mismo. */}
          {order.status === 'ASSIGNED' && (
            <View style={[styles.actionBtn, { backgroundColor: '#0066CC' }]}>
              <Text style={styles.actionBtnText}>Repartidor en camino a buscarlo</Text>
            </View>
          )}
          {order.status === 'PICKED_UP' && (
            <View style={[styles.actionBtn, { backgroundColor: '#00A86B' }]}>
              <Text style={styles.actionBtnText}>Recogido · va para el cliente</Text>
            </View>
          )}
          {order.status === 'DELIVERED' && (
            <View style={[styles.actionBtn, { backgroundColor: '#2E7D32' }]}>
              <Text style={styles.actionBtnText}>Entregado</Text>
            </View>
          )}
          {order.status === 'CANCELLED' && (
            <View style={[styles.actionBtn, { backgroundColor: '#9E9E9E' }]}>
              <Text style={styles.actionBtnText}>Cancelada</Text>
            </View>
          )}
        </View>
      </View>
    ));
  };

  // ── Stripe Connect onboarding handlers ──
  const handleOpenOnboarding = async () => {
    setRefreshingConnect(true);
    try {
      let url = restaurantInfo?.stripeOnboardingUrl;
      // El Account Link expira rápido (24h). Si no es reciente, pedimos uno nuevo.
      if (!url) {
        const resp = await callRefreshStripeOnboarding();
        url = resp.data.url;
      }
      if (url) {
        await Linking.openURL(url);
      } else {
        Alert.alert('Error', 'No pudimos generar el link de Stripe. Intenta de nuevo más tarde.');
      }
    } catch (e: any) {
      Alert.alert('Error', mensajeDeError(e, 'No pudimos abrir el registro de pagos de Stripe. Intenta de nuevo más tarde.'));
    } finally {
      setRefreshingConnect(false);
    }
  };

  const handleCheckConnectStatus = async () => {
    setRefreshingConnect(true);
    try {
      const resp = await callCheckRestaurantConnectStatus();
      const { chargesEnabled, detailsSubmitted } = resp.data;
      if (chargesEnabled && detailsSubmitted) {
        Alert.alert('¡Listo! 🎉', 'Tu cuenta Stripe está activa. Ya puedes recibir pagos con tarjeta.');
      } else if (detailsSubmitted) {
        Alert.alert('En revisión', 'Stripe está verificando tus datos. Esto puede tardar de minutos a horas.');
      } else {
        Alert.alert('Pendiente', 'Aún faltan datos por completar. Toca "Completar onboarding" para terminar.');
      }
    } catch (e: any) {
      Alert.alert('Error', mensajeDeError(e, 'No pudimos verificar tu cuenta de Stripe. Intenta de nuevo más tarde.'));
    } finally {
      setRefreshingConnect(false);
    }
  };

  // ── ESTADO DEL ANUNCIO (solo lectura) ─────────────────────────────────────
  // `premiumExpiresAt` es un Timestamp de Firestore. Si no viene, o no se puede
  // leer, NO se inventa una fecha plausible: la pantalla dice que no la tiene.
  const anuncioVenceMs: number | null = (() => {
    const v: any = restaurantInfo?.premiumExpiresAt;
    const ms = typeof v?.toMillis === 'function' ? v.toMillis() : null;
    return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
  })();
  // Vencido cuenta como apagado aunque el documento siga con `isPremium: true`:
  // el cron que lo apaga (functions/src/loyalty.ts) corre una vez al día, así
  // que puede haber horas en que el dato esté viejo. Mejor decir de menos.
  const anuncioActivo =
    !!restaurantInfo?.isPremium && (anuncioVenceMs === null || anuncioVenceMs > Date.now());
  const nombreDelAnuncio =
    restaurantInfo?.adPlan === 'BASICO'
      ? 'Anuncio Básico activo'
      : restaurantInfo?.adPlan === 'DESTACADO'
        ? 'Anuncio Destacado activo'
        : 'Anuncio activo';
  // Formato a mano (DD/MM/AAAA) en vez de toLocaleDateString: el motor de
  // Android no siempre trae los datos de la configuración regional es-DO y ahí
  // la fecha sale en inglés o en formato de EE. UU.
  const anuncioVenceTexto: string | null = (() => {
    if (anuncioVenceMs === null) return null;
    const d = new Date(anuncioVenceMs);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  })();

  // EL BANNER DE STRIPE PROMETÍA UN COBRO QUE HOY NO EXISTE. Decía "empezarás a
  // recibir pagos con tarjeta" y mandaba al dueño a rellenar el onboarding de
  // Stripe (datos personales, cuenta bancaria, 5 minutos de su tiempo)… para
  // nada: `CUSTOMER_CARD_ENABLED` está en false, así que en el carrito el
  // cliente NO tiene botón de tarjeta y ningún pedido puede pagarse así. El
  // dueño terminaba el trámite y seguía sin ver un solo pago con tarjeta.
  //
  // El banner y sus dos botones se conservan tal cual y vuelven solos el día
  // que la bandera pase a true; hasta entonces no se muestran.
  const needsConnectOnboarding =
    CUSTOMER_CARD_ENABLED &&
    restaurantInfo?.status === 'APPROVED' &&
    restaurantInfo?.stripeAccountId &&
    !restaurantInfo?.stripeChargesEnabled;

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* HEADER DASHBOARD */}
      <View style={styles.header}>
        <View>
          <Text style={styles.headerGreeting}>Panel de Control</Text>
          <Text style={styles.restaurantName} numberOfLines={1}>
            {restaurantInfo?.name ?? 'Tu Negocio'}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.statusToggle}
          onPress={() => {
            // Al tocar el badge de estado, explicamos qué significa cada valor.
            // Antes NO tenía onPress → Google Play rechazó por "Unresponsive UI
            // elements". El estado real lo controla el admin (approval flow),
            // por eso aquí no cambia — solo informa al dueño en qué fase está.
            const s = restaurantInfo?.status;
            if (s === 'APPROVED') {
              Alert.alert(
                'Negocio abierto',
                'Tu negocio está aprobado y visible para los clientes. Los pedidos entrarán al panel de PEDIDOS en tiempo real.'
              );
            } else if (s === 'PENDING_APPROVAL') {
              Alert.alert(
                'En revisión',
                'Enviamos tu solicitud al equipo. Te avisaremos por notificación cuando quede aprobado (normalmente 24 horas).'
              );
            } else {
              Alert.alert(
                'Sin registrar',
                'Aún no completaste el registro de tu negocio. Vamos a la pantalla de registro para terminar.',
                [
                  { text: 'Ahora no', style: 'cancel' },
                  { text: 'Registrar', onPress: () => router.push('/restaurant-registration') },
                ]
              );
            }
          }}
          activeOpacity={0.8}
        >
          <View
            style={[
              styles.onlineDot,
              { backgroundColor: restaurantInfo?.status === 'APPROVED' ? '#00C853' : '#FF9500' },
            ]}
          />
          <Text style={styles.statusText}>
            {restaurantInfo?.status === 'APPROVED'
              ? 'Abierto'
              : restaurantInfo?.status === 'PENDING_APPROVAL'
              ? 'En revisión'
              : 'Sin registrar'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* BANNER STRIPE CONNECT ONBOARDING */}
      {needsConnectOnboarding && (
        <View style={connectBannerStyles.container}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            <Ionicons name="card" size={26} color="#FF9500" style={{ marginRight: 12, marginTop: 2 }} />
            <View style={{ flex: 1 }}>
              <Text style={connectBannerStyles.title}>Activa pagos con tarjeta</Text>
              <Text style={connectBannerStyles.body}>
                Completa el onboarding de Stripe (5 min) y empezarás a recibir pagos con tarjeta directo en tu cuenta. Go Safely no cobra comisión por venta.
              </Text>
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
                <TouchableOpacity
                  style={connectBannerStyles.primaryBtn}
                  onPress={handleOpenOnboarding}
                  disabled={refreshingConnect}
                >
                  <Text style={connectBannerStyles.primaryBtnText}>
                    {refreshingConnect ? 'Cargando…' : 'Completar onboarding'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={connectBannerStyles.secondaryBtn}
                  onPress={handleCheckConnectStatus}
                  disabled={refreshingConnect}
                >
                  <Text style={connectBannerStyles.secondaryBtnText}>Ya lo hice</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      )}

      {/* TABS */}
      <ScrollView
        ref={filaDePestanas}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsScroll}
        contentContainerStyle={styles.tabsContainer}
        onLayout={(e) => (anchoDeLaFila.current = e.nativeEvent.layout.width)}
      >
        <TouchableOpacity
          onLayout={(e) => (medidaDePestana.current['ORDERS'] = e.nativeEvent.layout)}
          style={[styles.tab, activeTab === 'ORDERS' && styles.activeTab]}
          onPress={() => irAPestana('ORDERS')}
        >
          <Ionicons name="receipt" size={20} color={activeTab === 'ORDERS' ? Colors.primary : Colors.textSecondary} />
          <Text style={[styles.tabText, activeTab === 'ORDERS' && styles.activeTabText]}>Pedidos</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onLayout={(e) => (medidaDePestana.current['MENU'] = e.nativeEvent.layout)}
          style={[styles.tab, activeTab === 'MENU' && styles.activeTab]}
          onPress={() => irAPestana('MENU')}
        >
          <Ionicons name="restaurant" size={20} color={activeTab === 'MENU' ? Colors.primary : Colors.textSecondary} />
          <Text style={[styles.tabText, activeTab === 'MENU' && styles.activeTabText]}>Mi Catálogo</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onLayout={(e) => (medidaDePestana.current['STATS'] = e.nativeEvent.layout)}
          style={[styles.tab, activeTab === 'STATS' && styles.activeTab]}
          onPress={() => irAPestana('STATS')}
        >
          <Ionicons name="stats-chart" size={20} color={activeTab === 'STATS' ? Colors.primary : Colors.textSecondary} />
          <Text style={[styles.tabText, activeTab === 'STATS' && styles.activeTabText]}>Ventas</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onLayout={(e) => (medidaDePestana.current['PROMOS'] = e.nativeEvent.layout)}
          style={[styles.tab, activeTab === 'PROMOS' && styles.activeTab]}
          onPress={() => irAPestana('PROMOS')}
        >
          <Ionicons name="megaphone" size={20} color={activeTab === 'PROMOS' ? Colors.primary : Colors.textSecondary} />
          <Text style={[styles.tabText, activeTab === 'PROMOS' && styles.activeTabText]}>Ofertas</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onLayout={(e) => (medidaDePestana.current['PLANES'] = e.nativeEvent.layout)}
          style={[styles.tab, activeTab === 'PLANES' && styles.activeTab]}
          onPress={() => irAPestana('PLANES')}
        >
          {/* "Planes" con icono de tarjeta anunciaba una tienda dentro de la
              app. Ya no lo es: aquí solo se ve el estado del anuncio. */}
          <Ionicons name="star-outline" size={20} color={activeTab === 'PLANES' ? Colors.primary : Colors.textSecondary} />
          <Text style={[styles.tabText, activeTab === 'PLANES' && styles.activeTabText]}>Publicidad</Text>
        </TouchableOpacity>
      </ScrollView>

      {/* CONTENT */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <AvisoVistaPrevia que="el panel de un negocio" />
        {activeTab === 'ORDERS' && (
          <View>
            <Text style={styles.sectionTitle}>Nuevos (Por Aceptar)</Text>
            {renderOrdersList('PENDING')}
            
            <Text style={styles.sectionTitle}>En Preparación</Text>
            {renderOrdersList('PREPARING')}
            
            <Text style={styles.sectionTitle}>Listos (Esperando Repartidor)</Text>
            {renderOrdersList('READY')}

            {/* H65 — el pedido ya no se esfuma cuando lo toma un repartidor. */}
            <Text style={styles.sectionTitle}>En entrega</Text>
            {renderOrdersList(['ASSIGNED', 'PICKED_UP'])}

            <Text style={styles.sectionTitle}>Terminados hoy</Text>
            {renderOrdersList(['DELIVERED', 'CANCELLED'], { soloHoy: true })}
          </View>
        )}

        {activeTab === 'MENU' && (
          <View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 15, marginBottom: 15 }}>
              <Text style={[styles.sectionTitle, { marginTop: 0, marginBottom: 0 }]}>Mis Productos</Text>
              <TouchableOpacity style={[styles.addBtn, { paddingHorizontal: 12, paddingVertical: 8 }]} onPress={() => setAddProductModalVisible(true)}>
                <Text style={styles.addBtnText}>+ Nuevo</Text>
              </TouchableOpacity>
            </View>

            {menuItems.length === 0 ? (
              <View style={styles.placeholderContainer}>
                <Ionicons name="storefront-outline" size={64} color="#CCC" />
                <Text style={styles.placeholderTitle}>Tu catálogo está vacío</Text>
                <Text style={styles.placeholderText}>Sube tus productos para que los clientes puedan empezar a pedir.</Text>
              </View>
            ) : (
              menuItems.map((item, index) => {
                const imgs: string[] = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
                const primary = imgs[0] || item.imageUrl || null;
                return (
                  <View key={index} style={styles.menuItemCard}>
                    {/* IMAGEN PRINCIPAL + INDICADORES */}
                    <TouchableOpacity
                      onPress={() => primary && setFullScreenImage(primary)}
                      style={{ position: 'relative' }}
                      activeOpacity={0.85}
                    >
                      {primary ? (
                        <View style={{ position: 'relative' }}>
                          <Image
                            source={{ uri: primary }}
                            style={{
                              width: 90, height: 90,
                              borderRadius: 12,
                              backgroundColor: '#EEE',
                            }}
                            resizeMode="cover"
                          />
                          {imgs.length > 1 && (
                            <View style={{
                              position: 'absolute',
                              bottom: 6, right: 6,
                              backgroundColor: 'rgba(0,0,0,0.65)',
                              flexDirection: 'row',
                              alignItems: 'center',
                              paddingHorizontal: 5,
                              paddingVertical: 2,
                              borderRadius: 8,
                            }}>
                              <Ionicons name="images" size={10} color="#FFF" />
                              <Text style={{ fontSize: 10, color: '#FFF', marginLeft: 3, fontWeight: '700' }}>
                                {imgs.length}
                              </Text>
                            </View>
                          )}
                        </View>
                      ) : (
                        <View style={{
                          width: 90, height: 90,
                          borderRadius: 12,
                          backgroundColor: '#F5F5F5',
                          justifyContent: 'center', alignItems: 'center',
                          borderWidth: 1.5, borderColor: '#E0E0E0', borderStyle: 'dashed',
                        }}>
                          <Ionicons name="image-outline" size={26} color="#BBB" />
                          <Text style={{ fontSize: 9, color: '#999', marginTop: 4 }}>Sin foto</Text>
                        </View>
                      )}
                    </TouchableOpacity>

                    <View style={{ flex: 1, marginLeft: 14 }}>
                      <Text style={{ fontSize: 16, fontWeight: '700', color: Colors.text }} numberOfLines={1}>
                        {item.name}
                      </Text>
                      <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: 3, lineHeight: 16 }} numberOfLines={2}>
                        {item.description}
                      </Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                        <Text style={{ fontSize: 15, fontWeight: '800', color: Colors.primary }}>
                          {pesos(item.price)}
                        </Text>
                        {item.prepTime && (
                          <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 10 }}>
                            <Ionicons name="time-outline" size={11} color={Colors.textSecondary} />
                            <Text style={{ fontSize: 11, color: Colors.textSecondary, marginLeft: 3 }}>
                              {item.prepTime}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>

                    {/* CONTROLES DEL PLATO — antes esta columna solo mostraba una
                        etiqueta: no había forma de corregir un precio, marcar un
                        plato agotado ni borrarlo. Para un restaurante real eso
                        es descalificante: los precios cambian cada semana y el
                        pollo se acaba a las 8 de la noche. */}
                    <View style={{ alignItems: 'flex-end', justifyContent: 'space-between', height: 90 }}>
                      <TouchableOpacity
                        onPress={() => toggleDisponibilidad(index)}
                        style={[styles.statusBadge, {
                          backgroundColor: item.isAvailable ? '#E8F5E9' : '#FFEBEB',
                          flexDirection: 'row', alignItems: 'center',
                        }]}
                      >
                        <View style={{
                          width: 6, height: 6, borderRadius: 3,
                          backgroundColor: item.isAvailable ? '#2E7D32' : '#D32F2F',
                          marginRight: 5,
                        }} />
                        <Text style={{
                          fontSize: 10, fontWeight: 'bold',
                          color: item.isAvailable ? '#2E7D32' : '#D32F2F',
                        }}>
                          {item.isAvailable ? 'ACTIVO' : 'AGOTADO'}
                        </Text>
                      </TouchableOpacity>

                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity
                          onPress={() => cambiarPrecio(index)}
                          style={{ padding: 7, borderRadius: 9, backgroundColor: '#EEF4FF' }}
                          accessibilityLabel={`Cambiar precio de ${item.name}`}
                        >
                          <Ionicons name="pricetag-outline" size={17} color={Colors.primary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => borrarPlato(index)}
                          style={{ padding: 7, borderRadius: 9, backgroundColor: '#FFEBEB' }}
                          accessibilityLabel={`Borrar ${item.name}`}
                        >
                          <Ionicons name="trash-outline" size={17} color="#D32F2F" />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                );
              })
            )}
          </View>
        )}

        {activeTab === 'STATS' && (
          /* DOS MENTIRAS EN CUATRO LÍNEAS.
             (1) "Al operar con Go Safely bajo suscripción": el restaurante NO
                 paga ninguna suscripción — no existe tal cobro en ninguna parte
                 del código.
             (2) "RD$ 0 · Ventas esta semana" era un número ESCRITO A MANO. No
                 se calculaba de nada: un restaurante con ventas reales seguía
                 leyendo RD$ 0. Un dato inventado con pinta de dato es peor que
                 no mostrarlo, así que se quita y se dice qué hay en su lugar.
             El informe de ventas se construirá cuando haya de dónde sacarlo;
             mientras tanto el dueño tiene sus pedidos, uno por uno, en la
             pestaña Pedidos. */
          <View style={styles.placeholderContainer}>
            <Ionicons name="trending-up" size={64} color="#CCC" />
            <Text style={styles.placeholderTitle}>Informe de ventas</Text>
            <Text style={styles.placeholderText}>
              Todavía no está listo: esta versión no calcula tus totales por día ni por semana, y
              preferimos decírtelo a enseñarte un número que no es. Mientras tanto puedes ver cada
              pedido, con su monto, en la pestaña <Text style={{ fontWeight: 'bold' }}>Pedidos</Text>.
            </Text>
            <Text style={[styles.placeholderText, { marginBottom: 0 }]}>
              Lo que sí es seguro: Go Safely no te cobra comisión por venta. Cobras tú, y te quedas
              con todo.
            </Text>
          </View>
        )}

        {activeTab === 'PROMOS' && (
          <View style={{ paddingVertical: 15 }}>
            {/* ═══════════════════════════════════════════════════════════════
                ESTA PESTAÑA TENÍA DOS BOTONES Y NINGUNA OFERTA.
                ═══════════════════════════════════════════════════════════════
                Eran dos promociones fijas con los números clavados en el
                código —envío gratis sobre 500 pesos, 50% en el primer pedido—
                y, lo peor, NO DESCONTABAN NADA: el carrito y el servidor no
                miraban `activePromotions`. El cliente recibía el push
                «¡Delivery GRATIS!», entraba, y pagaba el envío completo.

                Ahora el negocio crea la suya, con sus propios números, y ve de
                un golpe lo que tiene puesto y qué hace cada una. Nosotros
                somos el puente: ponemos el mensajero y el cliente; la oferta
                la decide quien la paga. */}
            <Text style={styles.sectionTitle}>Tus ofertas</Text>
            <Text style={{ color: Colors.textSecondary, marginBottom: 16 }}>
              Tú decides la oferta y sus números. Se aplica sola en el carrito del cliente y sale
              en tu ficha para todo el que entre a verla. El aviso por notificación a los clientes
              de tu zona es otra cosa: solo sale si tienes el Anuncio Destacado activo, y como
              máximo uno cada 6 horas.
            </Text>

            {ofertas.length === 0 ? (
              <View style={[styles.orderCard, { alignItems: 'center', paddingVertical: 26 }]}>
                <Ionicons name="pricetags-outline" size={38} color={Colors.border} />
                <Text style={{ color: Colors.text, fontWeight: '700', marginTop: 10 }}>
                  Todavía no tienes ofertas
                </Text>
                <Text style={{ color: Colors.textSecondary, textAlign: 'center', marginTop: 6, paddingHorizontal: 10 }}>
                  Una oferta hace que te encuentren y que vuelvan. Puedes quitarla cuando quieras.
                </Text>
              </View>
            ) : (
              ofertas.map((oferta) => (
                <View
                  key={oferta.id}
                  style={[styles.orderCard, { flexDirection: 'row', alignItems: 'center', marginBottom: 10 }]}
                >
                  <View style={{ flex: 1, marginRight: 12 }}>
                    <Text style={{ fontSize: 16, fontWeight: 'bold', color: oferta.color || Colors.text }}>
                      {oferta.icon ? `${oferta.icon} ` : ''}{oferta.title}
                    </Text>
                    {/* Lo que la oferta HACE, escrito de los mismos números con
                        los que se calcula. Antes el texto se escribía a mano y
                        decía cosas que el código no cumplía. */}
                    <Text style={{ fontSize: 13, color: Colors.textSecondary, marginTop: 4 }}>
                      {resumenDeOferta(oferta)}
                    </Text>
                  </View>
                  <TouchableOpacity
                    disabled={guardandoPromo}
                    onPress={() => borrarOferta(oferta.id)}
                    style={{ padding: 9, borderRadius: 10, backgroundColor: '#FFEBEB', opacity: guardandoPromo ? 0.5 : 1 }}
                    accessibilityLabel={`Quitar la oferta ${oferta.title}`}
                  >
                    <Ionicons name="trash-outline" size={18} color="#D32F2F" />
                  </TouchableOpacity>
                </View>
              ))
            )}

            {/* Las dos formas de oferta, cada una en su botón: que vea sus
                opciones sin tener que descubrirlas. */}
            <Text style={[styles.sectionTitle, { marginTop: 22 }]}>Crear una oferta</Text>
            <TouchableOpacity
              disabled={guardandoPromo}
              onPress={() =>
                setOfertaNueva({
                  tipo: 'DELIVERY_GRATIS',
                  titulo: 'Delivery gratis',
                  minimo: '',
                  modo: 'PORCENTAJE',
                  porcentaje: '',
                  monto: '',
                  soloPrimerPedido: false,
                })
              }
              style={[styles.orderCard, { flexDirection: 'row', alignItems: 'center', opacity: guardandoPromo ? 0.5 : 1 }]}
            >
              <View style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: '#E8F5E9', alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons name="bicycle-outline" size={22} color="#2E7D32" />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: Colors.text }}>Delivery gratis</Text>
                <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: 2 }}>
                  Tú asumes el envío. Puedes pedir un mínimo de compra.
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={Colors.textSecondary} />
            </TouchableOpacity>

            <TouchableOpacity
              disabled={guardandoPromo}
              onPress={() =>
                setOfertaNueva({
                  tipo: 'DESCUENTO',
                  titulo: '',
                  minimo: '',
                  modo: 'PORCENTAJE',
                  porcentaje: '',
                  monto: '',
                  soloPrimerPedido: false,
                })
              }
              style={[styles.orderCard, { flexDirection: 'row', alignItems: 'center', marginTop: 10, opacity: guardandoPromo ? 0.5 : 1 }]}
            >
              <View style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: '#FFEBEB', alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons name="pricetag-outline" size={22} color="#D32F2F" />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: Colors.text }}>Descuento</Text>
                <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: 2 }}>
                  Un porcentaje o una cantidad fija. Puedes dejarlo solo para clientes nuevos.
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={Colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* ── EL FORMULARIO DE LA OFERTA ──────────────────────────────────── */}
        <Modal visible={!!ofertaNueva} transparent animationType="slide" onRequestClose={() => setOfertaNueva(null)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ScrollView keyboardShouldPersistTaps="handled">
                <Text style={styles.modalTitle}>
                  {ofertaNueva?.tipo === 'DELIVERY_GRATIS' ? 'Delivery gratis' : 'Descuento'}
                </Text>

                <Text style={styles.label}>¿Cómo se va a llamar? *</Text>
                <TextInput
                  style={styles.input}
                  placeholder={ofertaNueva?.tipo === 'DELIVERY_GRATIS' ? 'Ej. Delivery gratis este mes' : 'Ej. 20% para clientes nuevos'}
                  placeholderTextColor="#888"
                  value={ofertaNueva?.titulo ?? ''}
                  onChangeText={(v) => setOfertaNueva((f) => (f ? { ...f, titulo: v } : f))}
                />
                <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: -8, marginBottom: 12 }}>
                  Es lo que el cliente lee en tu ficha.
                </Text>

                {ofertaNueva?.tipo === 'DESCUENTO' && (
                  <>
                    <Text style={styles.label}>¿De cuánto?</Text>
                    <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
                      {(['PORCENTAJE', 'MONTO'] as const).map((modo) => {
                        const elegido = ofertaNueva?.modo === modo;
                        return (
                          <TouchableOpacity
                            key={modo}
                            onPress={() => setOfertaNueva((f) => (f ? { ...f, modo } : f))}
                            style={{
                              flex: 1, paddingVertical: 11, borderRadius: 10, alignItems: 'center',
                              borderWidth: elegido ? 2 : 1,
                              borderColor: elegido ? Colors.primary : Colors.border,
                              backgroundColor: elegido ? '#EEF4FF' : 'transparent',
                            }}
                          >
                            <Text style={{ fontWeight: elegido ? '700' : '500', color: elegido ? Colors.primary : Colors.text }}>
                              {modo === 'PORCENTAJE' ? 'Por porcentaje' : 'Cantidad fija'}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>

                    {ofertaNueva?.modo === 'PORCENTAJE' ? (
                      <>
                        <Text style={styles.label}>Porcentaje de descuento *</Text>
                        <TextInput
                          style={styles.input}
                          placeholder={`Entre 1 y ${PORCENTAJE_MAXIMO}`}
                          placeholderTextColor="#888"
                          keyboardType="number-pad"
                          value={ofertaNueva?.porcentaje ?? ''}
                          onChangeText={(v) => setOfertaNueva((f) => (f ? { ...f, porcentaje: v.replace(/[^0-9]/g, '') } : f))}
                        />
                      </>
                    ) : (
                      <>
                        <Text style={styles.label}>Pesos de descuento *</Text>
                        <TextInput
                          style={styles.input}
                          placeholder="Ej. 150"
                          placeholderTextColor="#888"
                          keyboardType="number-pad"
                          value={ofertaNueva?.monto ?? ''}
                          onChangeText={(v) => setOfertaNueva((f) => (f ? { ...f, monto: v.replace(/[^0-9]/g, '') } : f))}
                        />
                      </>
                    )}
                  </>
                )}

                <Text style={styles.label}>Compra mínima (opcional)</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Déjalo vacío si no quieres mínimo"
                  placeholderTextColor="#888"
                  keyboardType="number-pad"
                  value={ofertaNueva?.minimo ?? ''}
                  onChangeText={(v) => setOfertaNueva((f) => (f ? { ...f, minimo: v.replace(/[^0-9]/g, '') } : f))}
                />
                <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: -8, marginBottom: 12 }}>
                  Se mide sobre los productos, sin contar el envío ni la propina.
                </Text>

                <TouchableOpacity
                  onPress={() => setOfertaNueva((f) => (f ? { ...f, soloPrimerPedido: !f.soloPrimerPedido } : f))}
                  style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10 }}
                >
                  <Ionicons
                    name={ofertaNueva?.soloPrimerPedido ? 'checkbox' : 'square-outline'}
                    size={24}
                    color={ofertaNueva?.soloPrimerPedido ? Colors.primary : Colors.textSecondary}
                  />
                  <Text style={{ marginLeft: 10, flex: 1, color: Colors.text }}>
                    Solo la primera vez que cada cliente me compre
                  </Text>
                </TouchableOpacity>

                {/* Que vea EXACTAMENTE lo que va a leer el cliente, antes de
                    publicarlo. El texto sale de la misma regla que calcula el
                    descuento, así que no puede prometer otra cosa. */}
                {(() => {
                  const vista = normalizarOferta({
                    id: 'vista', type: ofertaNueva?.tipo, title: ofertaNueva?.titulo ?? '',
                    description: '', icon: '', color: '',
                    minimo: Number(ofertaNueva?.minimo) || 0,
                    porcentaje: ofertaNueva?.modo === 'PORCENTAJE' ? Number(ofertaNueva?.porcentaje) || 0 : 0,
                    monto: ofertaNueva?.modo === 'MONTO' ? Number(ofertaNueva?.monto) || 0 : 0,
                    soloPrimerPedido: ofertaNueva?.soloPrimerPedido === true,
                  });
                  if (!vista) return null;
                  return (
                    <View style={{ backgroundColor: '#F4FBF6', borderRadius: 12, padding: 12, marginTop: 6, marginBottom: 14 }}>
                      <Text style={{ fontSize: 11, fontWeight: '700', color: '#2E7D32', marginBottom: 4 }}>
                        ASÍ LO VA A VER EL CLIENTE
                      </Text>
                      <Text style={{ color: Colors.text }}>{resumenParaElCliente(vista)}</Text>
                    </View>
                  );
                })()}

                <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
                  <TouchableOpacity
                    onPress={() => setOfertaNueva(null)}
                    style={{ flex: 1, paddingVertical: 14, borderRadius: 12, borderWidth: 1, borderColor: Colors.border, alignItems: 'center' }}
                  >
                    <Text style={{ color: Colors.text, fontWeight: '600' }}>Cancelar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    disabled={guardandoPromo}
                    onPress={crearOferta}
                    style={{ flex: 1, paddingVertical: 14, borderRadius: 12, backgroundColor: Colors.primary, alignItems: 'center', opacity: guardandoPromo ? 0.6 : 1 }}
                  >
                    <Text style={{ color: '#FFF', fontWeight: '700' }}>
                      {guardandoPromo ? 'Guardando…' : 'Publicar oferta'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </View>
          </View>
        </Modal>

        {activeTab === 'PLANES' && (
          <View style={{ paddingVertical: 15 }}>
            {/* ═══════════════════════════════════════════════════════════════
                ESTA PESTAÑA ERA UNA TIENDA. AHORA SOLO INFORMA.
                ═══════════════════════════════════════════════════════════════
                Vendía dentro de la app dos planes de anuncio (RD$1,000 y
                RD$2,000 por 30 días) cobrados por transferencia y por tarjeta.
                Lo que se compraba se consume dentro de la app —posición #1 en
                la categoría, etiqueta de "Recomendado", banner—, y para eso
                Google exige su propia facturación: cobrarlo por fuera es motivo
                de rechazo. La contratación se hace ahora hablando con nosotros;
                aquí el dueño ve su estado, que es información suya. */}
            <Text style={styles.sectionTitle}>Publicidad</Text>

            {anuncioActivo ? (
              <View style={[styles.orderCard, { borderColor: '#FF9500', borderWidth: 2, backgroundColor: '#FFFDF7' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Ionicons name="star" size={20} color="#FF9500" />
                  <Text style={{ fontSize: 17, fontWeight: '800', color: '#E65100', marginLeft: 8 }}>
                    {nombreDelAnuncio}
                  </Text>
                </View>
                <Text style={{ fontSize: 13.5, color: Colors.text, lineHeight: 19 }}>
                  Tu negocio aparece por encima de los que no tienen anuncio dentro de su
                  categoría.
                </Text>
                {/* La fecha sale del documento del restaurante. Si el servidor
                    no la trae, NO se rellena con una plausible: se dice. */}
                <Text style={{ fontSize: 13.5, color: Colors.textSecondary, marginTop: 8 }}>
                  {anuncioVenceTexto
                    ? `Vence el ${anuncioVenceTexto}.`
                    : 'No pudimos leer la fecha de vencimiento. Escríbenos y te la confirmamos.'}
                </Text>
              </View>
            ) : (
              <View style={[styles.orderCard, { borderColor: '#E3E8EF', borderWidth: 1.5 }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Ionicons name="megaphone-outline" size={20} color={Colors.textSecondary} />
                  <Text style={{ fontSize: 17, fontWeight: '800', color: Colors.text, marginLeft: 8 }}>
                    No tienes publicidad activa
                  </Text>
                </View>
                <Text style={{ fontSize: 13.5, color: Colors.textSecondary, lineHeight: 19 }}>
                  Tu negocio aparece igual en su categoría y recibe pedidos con normalidad. La
                  publicidad solo sirve para salir por delante de los demás.
                </Text>
              </View>
            )}

            <View style={{ backgroundColor: '#E8F5E9', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#A5D6A7', flexDirection: 'row', alignItems: 'center' }}>
              <Ionicons name="shield-checkmark" size={28} color="#2E7D32" />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={{ fontWeight: '800', color: '#1B5E20', fontSize: 14 }}>
                  Recibir pedidos es gratis
                </Text>
                {/* Antes decía "El 100% del pedido va directo a tu cuenta",
                    que insinuaba un depósito automático que no existe: hoy el
                    cliente paga en efectivo al recibir. Lo cierto —y lo que
                    importa— es que Go Safely no se queda con nada. */}
                <Text style={{ color: '#2E7D32', fontSize: 12, marginTop: 2 }}>
                  Go Safely no te cobra comisión por venta. El cliente te paga a ti; el dinero no
                  pasa por nosotros.
                </Text>
              </View>
            </View>

            {/* ⚠️ ESTA TARJETA ENTERA DESAPARECE EN iOS.
                Es una invitación a contratar por WhatsApp el Plan Destacado,
                que pone al restaurante en la sección «Destacados» DE LA APP.
                Visibilidad dentro de la app comprada fuera de la compra de
                Apple: 3.1.1. Y no se salva por la excepción 3.1.3(g), que solo
                cubre apps cuyo ÚNICO propósito es gestionar campañas.
                Lo que SÍ se queda en iOS es el estado del anuncio que ya tenga
                contratado: eso es información suya, no una compra.
                Ver COBROS_EN_LA_APP en constants/featureFlags.ts. */}
            {COBROS_EN_LA_APP && (
            <View style={[styles.orderCard, { borderColor: '#7C4DFF', borderWidth: 1.5, backgroundColor: '#FBF9FF' }]}>
              <Text style={{ fontSize: 16, fontWeight: '800', color: '#5A2DBF', marginBottom: 6 }}>
                ¿Quieres contratar publicidad?
              </Text>
              <Text style={{ fontSize: 13.5, color: Colors.text, lineHeight: 19, marginBottom: 10 }}>
                La publicidad no se contrata desde la app: escríbenos por WhatsApp y te explicamos
                los planes, lo que incluye cada uno y el precio. Nosotros la activamos y la verás
                aquí mismo.
              </Text>
              <TouchableOpacity
                onPress={contratarAnuncioPorWhatsApp}
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 13, borderRadius: 11, backgroundColor: '#25D366' }}
              >
                <Ionicons name="logo-whatsapp" size={19} color="#FFF" />
                <Text style={{ color: '#FFF', fontWeight: '800', marginLeft: 9, fontSize: 14 }}>
                  Escribirnos por WhatsApp
                </Text>
              </TouchableOpacity>
            </View>
            )}

            <Text style={{ fontSize: 11, color: Colors.textSecondary, textAlign: 'center', marginTop: 6, paddingHorizontal: 20, fontStyle: 'italic' }}>
              La publicidad es opcional y no es una suscripción: nada se te cobra automáticamente ni
              desde la app. Recibir pedidos es gratis siempre, la contrates o no.
            </Text>
          </View>
        )}
      </ScrollView>

      {/* ══════════════════════════════════════════════════════════════════
          MI PERFIL — la puerta que a este rol le faltaba
          ══════════════════════════════════════════════════════════════════
          El botón de «Eliminar Mi Cuenta» vive solo en `(tabs)/profile.tsx`.
          Pero `_layout.tsx` manda al RESTAURANT_OWNER a esta pantalla, que es
          del stack raíz: sin cabecera, sin «atrás» (se entra con `replace`) y
          sin un solo enlace al perfil. Resultado: una cuenta real, con el
          número COMPLETO de cédula del dueño guardado —la única excepción de
          toda la app, según privacy.tsx— y SIN NINGUNA vía de borrado.

          Y lo que lo remata: `privacy.tsx` y `terms.tsx` le dicen por escrito
          que elimine su cuenta «desde Perfil → Eliminar Mi Cuenta». Para este
          rol esa ruta no existía, así que la política de privacidad le estaba
          dando una instrucción imposible de seguir. La guideline 5.1.1(v)
          exige que el borrado se pueda iniciar y completar DENTRO de la app,
          para toda cuenta que la app permita crear — no para algunos roles.

          La ruta no está bloqueada: la guardia de aislamiento de
          `_layout.tsx` solo filtra /restaurant-dashboard, /school-* y
          /crm-settings, y su propio comentario dice que las rutas comunes
          (profile, cart, kyc-*) no se tocan. */}
      <TouchableOpacity
        style={styles.perfilBtn}
        onPress={() => router.push('/(tabs)/profile')}
        accessibilityRole="button"
        accessibilityLabel="Mi perfil y ajustes de cuenta"
      >
        <Ionicons name="person-circle-outline" size={26} color="#FFF" />
      </TouchableOpacity>

      {/* FLOATING LOGOUT */}
      <TouchableOpacity
        style={styles.logoutBtn}
        onPress={async () => {
          // H01, auditoría 16-sep-2026 — Este botón cerraba solo el SDK nativo
          // y ponía cuatro campos en null. La caché REST quedaba viva (al
          // siguiente arranque el dueño "seguía dentro"), el token de push
          // seguía en su documento (los avisos de pedidos le llegaban a quien
          // usara el teléfono después) y nombre, teléfono, correo y foto se
          // quedaban guardados en disco. `authBridge.signOut()` es el cierre
          // completo que usan todos los botones de salir.
          if (saliendoRef.current) return;
          saliendoRef.current = true;
          try {
            await authBridge.signOut();
          } catch (e) {
            console.warn('[restaurant-dashboard] signOut falló:', e);
          } finally {
            saliendoRef.current = false;
          }
          router.replace('/');
        }}
      >
        <Ionicons name="log-out" size={24} color="#FFF" />
      </TouchableOpacity>

      {/* MODAL PARA AGREGAR PRODUCTO */}
      {/* onRequestClose (H37): sin él, en Android el botón Atrás se tragaba con
          el modal abierto y no pasaba nada — la app parecía congelada. Cierra
          igual que la X; lo escrito se conserva, como al tocarla. */}
      <Modal
        visible={isAddProductModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setAddProductModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Nuevo Producto</Text>
              <TouchableOpacity onPress={() => setAddProductModalVisible(false)}>
                <Ionicons name="close" size={24} color={Colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
                <Text style={styles.label}>Fotos del Plato *</Text>
                <Text style={{ fontSize: 11, color: Colors.textSecondary, fontWeight: '600' }}>
                  {newProduct.images.filter((u: any) => !!u).length}/3
                </Text>
              </View>
              <Text style={{ fontSize: 11, color: Colors.textSecondary, marginBottom: 10, fontStyle: 'italic' }}>
                Sube fotos de calidad — buena iluminación y plato bien presentado. La primera será la principal.
              </Text>
              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 18 }}>
                {[0, 1, 2].map((i) => {
                  const hasImg = !!newProduct.images[i];
                  return (
                    <TouchableOpacity
                      key={i}
                      style={{
                        flex: 1,
                        aspectRatio: 4 / 3,
                        borderRadius: 12,
                        borderWidth: hasImg ? 0 : 2,
                        borderColor: i === 0 ? Colors.primary : '#E0E0E0',
                        borderStyle: 'dashed',
                        backgroundColor: hasImg ? '#000' : '#FAFAFA',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        shadowColor: '#000',
                        shadowOpacity: hasImg ? 0.15 : 0,
                        shadowOffset: { width: 0, height: 2 },
                        shadowRadius: 4,
                        elevation: hasImg ? 3 : 0,
                      }}
                      activeOpacity={0.85}
                      onPress={async () => {
                        try {
                          const result = await ImagePicker.launchImageLibraryAsync({
                            mediaTypes: ['images'],
                            allowsEditing: true,
                            aspect: [4, 3],
                            quality: 0.85,
                          });
                          if (!result.canceled && result.assets && result.assets.length > 0) {
                            const localUri = result.assets[0].uri;
                            // Preview local inmediato.
                            setNewProduct((prev) => {
                              const imgs = [...prev.images];
                              imgs[i] = localUri;
                              return { ...prev, images: imgs };
                            });
                            // FIX AUDIT — Antes se guardaba el URI LOCAL (file://)
                            // en el menú → el cliente veía la imagen ROTA. Ahora
                            // subimos a Storage y reemplazamos por la URL pública.
                            if (userFirebaseUid) {
                              try {
                                // FIX — La carpeta era `restaurant_menu/`, pero
                                // storage.rules solo autoriza `menu_images/`
                                // (y termina en denegar todo lo demás). O sea
                                // que la subida SIEMPRE fallaba y el plato se
                                // guardaba con el file:// local: el cliente
                                // nunca veía la foto. Carpeta correcta:
                                const url = await storageService.uploadToPath(
                                  `menu_images/${userFirebaseUid}/${Date.now()}_${i}.jpg`,
                                  localUri
                                );
                                setNewProduct((prev) => {
                                  const imgs = [...prev.images];
                                  imgs[i] = url;
                                  return { ...prev, images: imgs };
                                });
                              } catch (upErr) {
                                console.error('menu image upload', upErr);
                                Alert.alert('Imagen no subida', 'La foto no se pudo subir a la nube. Intenta de nuevo antes de guardar el producto.');
                              }
                            }
                          }
                        } catch (e) {
                          console.error('Error seleccionando imagen', e);
                        }
                      }}
                    >
                      {hasImg ? (
                        <View style={{ width: '100%', height: '100%' }}>
                          <Image
                            source={{ uri: newProduct.images[i] }}
                            style={{ width: '100%', height: '100%' }}
                            resizeMode="cover"
                          />
                          {/* Gradient overlay para legibilidad */}
                          <View style={{
                            position: 'absolute',
                            bottom: 0, left: 0, right: 0,
                            height: 24,
                            backgroundColor: 'rgba(0,0,0,0.45)',
                          }} />
                          {i === 0 && (
                            <View style={{
                              position: 'absolute', top: 6, left: 6,
                              backgroundColor: Colors.primary,
                              paddingHorizontal: 6, paddingVertical: 2,
                              borderRadius: 4,
                            }}>
                              <Text style={{ fontSize: 9, color: '#FFF', fontWeight: '800' }}>PRINCIPAL</Text>
                            </View>
                          )}
                          {/* Eliminar */}
                          <TouchableOpacity
                            onPress={() => {
                              const newImages = [...newProduct.images];
                              newImages[i] = '';
                              setNewProduct({ ...newProduct, images: newImages.filter(Boolean) });
                            }}
                            style={{
                              position: 'absolute', top: 6, right: 6,
                              backgroundColor: 'rgba(0,0,0,0.55)',
                              width: 22, height: 22, borderRadius: 11,
                              justifyContent: 'center', alignItems: 'center',
                            }}
                          >
                            <Ionicons name="close" size={14} color="#FFF" />
                          </TouchableOpacity>
                          {/* Ampliar */}
                          <TouchableOpacity
                            onPress={() => setFullScreenImage(newProduct.images[i])}
                            style={{
                              position: 'absolute', bottom: 4, right: 6,
                              flexDirection: 'row', alignItems: 'center',
                            }}
                          >
                            <Ionicons name="expand-outline" size={12} color="#FFF" />
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <View style={{ alignItems: 'center' }}>
                          <Ionicons
                            name={i === 0 ? 'camera' : 'add'}
                            size={i === 0 ? 26 : 22}
                            color={i === 0 ? Colors.primary : '#BBB'}
                          />
                          <Text style={{
                            fontSize: 10,
                            color: i === 0 ? Colors.primary : Colors.textSecondary,
                            marginTop: 4,
                            fontWeight: i === 0 ? '700' : '500',
                          }}>
                            {i === 0 ? 'Principal' : `Foto ${i + 1}`}
                          </Text>
                        </View>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>

              <Text style={styles.label}>Nombre del Producto *</Text>
              <TextInput style={styles.input} placeholder="Ej. Hamburguesa Doble" value={newProduct.name} onChangeText={(t) => setNewProduct({...newProduct, name: t})} />

              <Text style={styles.label}>Descripción *</Text>
              <TextInput style={[styles.input, { height: 60, textAlignVertical: 'top' }]} placeholder="Ingredientes, detalles..." multiline value={newProduct.description} onChangeText={(t) => setNewProduct({...newProduct, description: t})} />

              <View style={{ flexDirection: 'row', gap: 10 }}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.label}>Precio (RD$) *</Text>
                  <TextInput style={styles.input} placeholder="Ej. 450" keyboardType="numeric" value={newProduct.price} onChangeText={(t) => setNewProduct({...newProduct, price: t})} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.label}>Tiempo Prep. *</Text>
                  <TextInput style={styles.input} placeholder="Ej. 15 min" value={newProduct.prepTime} onChangeText={(t) => setNewProduct({...newProduct, prepTime: t})} />
                </View>
              </View>

              <Text style={styles.label}>Categoría *</Text>
              <TextInput style={styles.input} placeholder="Ej. Platos Principales" value={newProduct.category} onChangeText={(t) => setNewProduct({...newProduct, category: t})} />

              <Text style={styles.label}>¿Disponible ahora mismo?</Text>
              <View style={styles.availabilityRow}>
                <TouchableOpacity 
                  style={[styles.toggleBtn, { flex: 1, marginRight: 5 }, newProduct.isAvailable ? styles.toggleOn : styles.toggleOff]}
                  onPress={() => setNewProduct({...newProduct, isAvailable: true})}
                >
                  <Text style={{ color: newProduct.isAvailable ? '#FFF' : Colors.textSecondary, fontWeight: 'bold', textAlign: 'center' }}>
                    SÍ (Disponible)
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.toggleBtn, { flex: 1, marginLeft: 5 }, !newProduct.isAvailable ? { backgroundColor: '#FF3B30' } : styles.toggleOff]}
                  onPress={() => setNewProduct({...newProduct, isAvailable: false})}
                >
                  <Text style={{ color: !newProduct.isAvailable ? '#FFF' : Colors.textSecondary, fontWeight: 'bold', textAlign: 'center' }}>
                    NO (Agotado)
                  </Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity 
                style={styles.saveBtn} 
                onPress={async () => {
                  // El alta de plato escribe DIRECTO con updateRestaurantMenu, sin
                  // pasar por `guardarMenu`, asi que necesita su propio candado.
                  // Ver la nota larga de `guardarMenu`.
                  if (bloquearSiInvitado('publicar un plato en tu menu')) return;
                  if(!newProduct.name || !newProduct.price) return Alert.alert('Error', 'Nombre y Precio son obligatorios');
                  if(newProduct.images.length === 0) return Alert.alert('Error', 'Debes subir al menos 1 foto del plato');
                  if(!userFirebaseUid) return Alert.alert('Sesión requerida', 'Inicia sesión como dueño del negocio para guardar productos.');

                  // Ninguna foto puede quedarse en file:// — esa ruta solo existe
                  // dentro de este teléfono, así que el cliente vería un hueco.
                  // Antes se guardaba igual y el plato salía roto en el catálogo
                  // sin que el dueño se enterara nunca.
                  if (newProduct.images.some((u) => u && !/^https?:\/\//i.test(u))) {
                    return Alert.alert(
                      'Espera a que suban las fotos',
                      'Alguna foto todavía no terminó de subir (o falló). Si el problema sigue, quítala y vuelve a elegirla — sin subirla, el cliente vería el plato sin imagen.'
                    );
                  }

                  // FIX AUDITORÍA — El precio venía del TextInput como STRING, así
                  // que la validación anti-manipulación del servidor (typeof price
                  // === 'number') quedaba inerte. Lo guardamos como NÚMERO.
                  const priceNum = aMonto(newProduct.price);
                  if (!Number.isFinite(priceNum) || priceNum <= 0) {
                    return Alert.alert('Error', 'El precio debe ser un número válido (ej. 450).');
                  }

                  // ID ESTABLE POR PLATO — antes se guardaba sin `id`. En el
                  // carrito del cliente eso hacía que `a.id === b.id` fuera
                  // `undefined === undefined` (true): dos platos distintos se
                  // fusionaban en uno y el basurero borraba el carrito
                  // completo. El id se genera aquí, una sola vez, y no cambia
                  // aunque el dueño edite el nombre o el precio después.
                  const idPlato = `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

                  // ⚠️ AQUI SE ANUNCIABA «EXITO» SIN ESPERAR A NADA.
                  //
                  // Era updateRestaurantMenu(...).catch(e => console.error(e))
                  // —a fuego y olvido, con el error escondido en la consola— y
                  // el Alert de «Éxito» INCONDICIONAL justo después. Si la
                  // escritura fallaba (datos flojos, reglas, token vencido), el
                  // dueño leía «Producto agregado a tu menú», veía el plato en
                  // la lista, cerraba el modal… y al volver a entrar no estaba.
                  // Peor: podía pasarse la tarde subiendo fotos y precios de un
                  // menú que no se guardó.
                  //
                  // Lo curioso es que el arreglo YA EXISTÍA EN ESTE ARCHIVO:
                  // editar precio, agotar y borrar usan guardarMenu, que
                  // espera, revierte la pantalla y avisa — con el comentario
                  // «revertir: que la pantalla no mienta». Al alta nunca se le
                  // aplicó.
                  const menuAnterior = menuItems;
                  const updatedMenu = [...menuItems, { ...newProduct, id: idPlato, price: priceNum }];
                  setMenuItems(updatedMenu);
                  try {
                    await firestoreService.updateRestaurantMenu(userFirebaseUid, updatedMenu);
                  } catch (e) {
                    console.error('[menu] no se pudo guardar el plato nuevo:', e);
                    setMenuItems(menuAnterior); // revertir: que la pantalla no mienta
                    Alert.alert(
                      'El plato no se guardó',
                      'Revisa tu conexión y vuelve a tocar «Guardar Producto». Lo que escribiste sigue aquí.'
                    );
                    return; // ⚠️ NO se limpia el modal: perdería su trabajo.
                  }

                  Alert.alert('Éxito', 'Producto agregado a tu menú.');

                  // Limpiar modal — solo cuando de verdad se guardó.
                  setNewProduct({ name: '', description: '', price: '', category: '', prepTime: '', isAvailable: true, images: [] });
                  setAddProductModalVisible(false);
                }}
              >
                <Text style={styles.saveBtnText}>Guardar Producto</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* CAMBIAR PRECIO — modal propio porque Alert.prompt solo existe en iOS */}
      <Modal visible={!!precioEditando} transparent animationType="fade" onRequestClose={() => setPrecioEditando(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', padding: 28 }}>
          <View style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 22 }}>
            <Text style={{ fontSize: 17, fontWeight: '800', color: Colors.text }}>
              Nuevo precio
            </Text>
            <Text style={{ fontSize: 13, color: Colors.textSecondary, marginTop: 6, marginBottom: 16 }}>
              {precioEditando != null ? menuItems[precioEditando.index]?.name : ''} · escribe solo números
            </Text>
            <TextInput
              value={precioEditando?.valor ?? ''}
              onChangeText={(t) => setPrecioEditando((p) => (p ? { ...p, valor: t } : p))}
              keyboardType="numeric"
              autoFocus
              placeholder="450"
              placeholderTextColor="#BBB"
              style={{
                borderWidth: 1.5, borderColor: '#E3E8EF', borderRadius: 11,
                paddingHorizontal: 14, paddingVertical: 13,
                fontSize: 22, fontWeight: '700', color: Colors.text,
              }}
            />
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 20 }}>
              <TouchableOpacity
                onPress={() => setPrecioEditando(null)}
                style={{ flex: 1, paddingVertical: 14, borderRadius: 11, backgroundColor: '#F1F3F7', alignItems: 'center' }}
              >
                <Text style={{ fontWeight: '700', color: Colors.textSecondary }}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={confirmarPrecio}
                style={{ flex: 1, paddingVertical: 14, borderRadius: 11, backgroundColor: Colors.primary, alignItems: 'center' }}
              >
                <Text style={{ fontWeight: '800', color: '#FFF' }}>Guardar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* FULL SCREEN IMAGE VIEWER */}
      <Modal
        visible={!!fullScreenImage}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setFullScreenImage(null)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', justifyContent: 'center', alignItems: 'center' }}>
          <TouchableOpacity 
            style={{ position: 'absolute', top: 50, right: 20, zIndex: 10, padding: 10 }}
            onPress={() => setFullScreenImage(null)}
          >
            <Ionicons name="close-circle" size={40} color="#FFF" />
          </TouchableOpacity>
          {fullScreenImage && (
            <Image 
              source={{ uri: fullScreenImage }} 
              style={{ width: '100%', height: '80%', resizeMode: 'contain' }} 
            />
          )}
        </View>
      </Modal>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F5F7FA' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: '#0A2E73' },
  headerGreeting: { color: 'rgba(255,255,255,0.7)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
  restaurantName: { color: '#FFF', fontSize: 20, fontWeight: 'bold' },
  statusToggle: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 },
  onlineDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#00C853', marginRight: 6 },
  statusText: { color: '#FFF', fontSize: 12, fontWeight: 'bold' },

  tabsScroll: { flexGrow: 0, backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E0E0E0' },
  tabsContainer: { flexDirection: 'row', minWidth: '100%' },
  // MEDIDO EL 3-OCT-2026 EN UN TELÉFONO (375 px): las cinco pestañas sumaban
  // 590 px. «Publicidad» —la única cosa por la que un negocio nos paga— quedaba
  // entera fuera de pantalla, y de «Ofertas» se veían 31 px: ni el texto. La
  // fila SÍ se desliza, pero sin indicador y sin ninguna etiqueta cortada a la
  // vista, nada dice que haya más. Con 11 de relleno bajan a ~480 px y
  // «Ofertas» queda cortada por la mitad, que es la pista que la gente entiende.
  tab: { paddingHorizontal: 11, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 15, gap: 5 },
  activeTab: { borderBottomWidth: 3, borderBottomColor: Colors.primary },
  tabText: { fontSize: 14, color: Colors.textSecondary, fontWeight: '600' },
  activeTabText: { color: Colors.primary, fontWeight: 'bold' },

  content: { flex: 1, padding: 20 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: Colors.text, marginBottom: 10, marginTop: 15 },
  
  orderCard: { backgroundColor: '#FFF', borderRadius: 12, padding: 15, marginBottom: 15, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 2 },
  orderHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
  orderId: { fontSize: 16, fontWeight: 'bold', color: Colors.primary },
  orderTime: { fontSize: 12, color: Colors.textSecondary },
  orderCustomer: { fontSize: 14, color: Colors.text, marginBottom: 4 },
  orderItems: { fontSize: 14, color: Colors.text, marginBottom: 4 },
  orderTotal: { fontSize: 14, fontWeight: 'bold', color: Colors.accent, marginBottom: 15 },
  actionBtn: { paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  actionBtnText: { color: '#FFF', fontWeight: 'bold', fontSize: 14 },

  emptyState: { backgroundColor: '#FFF', padding: 20, borderRadius: 12, alignItems: 'center', borderWidth: 1, borderColor: '#E0E0E0', borderStyle: 'dashed' },
  emptyStateText: { color: Colors.textSecondary, fontStyle: 'italic' },

  placeholderContainer: { alignItems: 'center', justifyContent: 'center', marginTop: 40, padding: 20 },
  placeholderTitle: { fontSize: 20, fontWeight: 'bold', color: Colors.text, marginTop: 15, marginBottom: 10 },
  placeholderText: { textAlign: 'center', color: Colors.textSecondary, lineHeight: 22, marginBottom: 20 },
  addBtn: { backgroundColor: Colors.primary, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 8 },
  addBtnText: { color: '#FFF', fontWeight: 'bold' },
  statsCard: { backgroundColor: '#E8F5E9', width: '100%', padding: 20, borderRadius: 12, alignItems: 'center', marginTop: 20, borderWidth: 1, borderColor: '#A5D6A7' },
  statsValue: { fontSize: 28, fontWeight: 'bold', color: '#2E7D32' },
  statsLabel: { color: '#388E3C', marginTop: 5 },

  // Justo encima del de cerrar sesión, mismo tamaño y misma esquina: son las
  // dos acciones de cuenta de esta pantalla y se leen juntas.
  perfilBtn: { position: 'absolute', bottom: 90, right: 20, backgroundColor: '#0A2540', width: 50, height: 50, borderRadius: 25, justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 5, elevation: 5 },
  logoutBtn: { position: 'absolute', bottom: 30, right: 20, backgroundColor: '#FF3B30', width: 50, height: 50, borderRadius: 25, justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 5, elevation: 5 },

  // Modal Styles
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContent: { backgroundColor: '#FFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, height: '85%', padding: 20 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: Colors.text },
  photoUploadBox: { backgroundColor: '#F9F9F9', borderWidth: 2, borderColor: '#E0E0E0', borderStyle: 'dashed', borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  
  menuItemCard: { flexDirection: 'row', backgroundColor: '#FFF', padding: 15, borderRadius: 12, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 2 },
  menuItemImgPlaceholder: { width: 60, height: 60, backgroundColor: '#F5F5F5', borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  
  label: { fontSize: 13, fontWeight: '600', color: Colors.text, marginBottom: 5, marginTop: 10 },
  input: { backgroundColor: '#F5F5F5', borderRadius: 8, padding: 12, fontSize: 14, color: Colors.text, borderWidth: 1, borderColor: '#E0E0E0' },
  availabilityRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 10, backgroundColor: '#F9F9F9', padding: 15, borderRadius: 8 },
  toggleBtn: { paddingHorizontal: 20, paddingVertical: 8, borderRadius: 20 },
  toggleOn: { backgroundColor: '#00C853' },
  toggleOff: { backgroundColor: '#E0E0E0' },
  saveBtn: { backgroundColor: Colors.primary, padding: 15, borderRadius: 12, alignItems: 'center', marginTop: 30, marginBottom: 40 },
  saveBtnText: { color: '#FFF', fontWeight: 'bold', fontSize: 16 },
  orderActions: { flexDirection: 'row', gap: 10, marginTop: 15 }
});

const connectBannerStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFF8E1',
    borderBottomWidth: 1,
    borderBottomColor: '#FFCC80',
    padding: 14,
  },
  title: { fontSize: 15, fontWeight: 'bold', color: '#E65100', marginBottom: 4 },
  body: { fontSize: 12, color: '#333', lineHeight: 17 },
  primaryBtn: { backgroundColor: '#FF9500', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  primaryBtnText: { color: '#FFF', fontWeight: 'bold', fontSize: 13 },
  secondaryBtn: { backgroundColor: 'transparent', borderWidth: 1, borderColor: '#FF9500', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  secondaryBtnText: { color: '#FF9500', fontWeight: 'bold', fontSize: 13 },
});
